import { Body, Box, Link, ListItem, UnorderedList } from '@hover/blueprint';
import React from 'react';
import SectionContainer from '../SectionContainer';

export const ContentLatest = () => (
  <>
    <SectionContainer
      id="privacy-personal-information"
      heading="Information we collect"
    >
      <Body mb={500}>
        We collect information from you, from HOVER users, and automatically
        through your use of our services.
      </Body>
      <Body mb={500}>
        <b>When you create an account:</b> When you create a HOVER account, you
        provide us with information, such as your name, your email address, your
        phone number, and a password. You may also choose to add a profile photo
        and payment information to your account.
      </Body>
      <Body mb={500}>
        <b>When you use our services:</b> When you use HOVER to create a model
        of a home or a room, you provide the property’s postal address and
        images or blueprints of the structure. You may also provide other
        information through free-form text fields in our applications. The
        images we collect may be still photographs or videos. Please take care
        to ensure the property images you provide to HOVER do not contain any
        information that itself can identify you or any other individual. We
        collect and store applicable multimedia content, such as images, videos,
        and audio recordings provided to HOVER, as well as associated metadata
        and sensor information from your device (for example, image metadata and
        the location where the image was taken) in order to create a 3D model.
        We may also collect device information (such as the model number of your
        phone to determine the camera specifications for collected images), or
        approximate (or coarse) location information (such as when more precise
        location services are not utilized or to determine relative position).
        When you order a HOVER Rendering of a home or use HOVER’s Design Studio
        feature, you may provide us with additional information about the home
        or its occupants, such as design choices or preferences.
      </Body>
      <Body mb={500}>
        <b>
          When a HOVER user provides information about a home that you own or in
          which you reside:
        </b>{' '}
        HOVER works with many businesses that provide property services,
        including home improvement contractors, insurance carriers and
        adjusters, and others. When a professional HOVER user orders a HOVER
        service for a home that you own or in which you reside, we collect some
        or all of the information listed above.
      </Body>
      <Body mb={500}>
        <b>When you visit our sites or apps:</b> We collect information about
        how you interact with our sites and apps, including when you visit our
        sites without logging into your account. This may include log data (such
        as your device ID, IP address, browser information, device hardware, and
        software type), usage data (such as which pages you click on or how you
        use our mobile app), geo-location information, and cookie data (see
        below for more information about cookies).
      </Body>

      <Body mb={500}>
        <b>When you contact sales or customer support:</b> When you speak with a
        HOVER sales or customer-support representative via phone or video, we
        may record the audio and video of your conversation. You may also
        provide our sales representatives with additional information about your
        business and your customers.
      </Body>

      <Body mb={500}>
        <b>Information we collect from third parties:</b> We may also
        periodically obtain both personal and non-personal information about you
        from third parties, such as marketing partners who provide us with
        information about potential customers of our business services, or
        public sources, such as information on a website.
      </Body>
      <Body mb={500}>
        <b>
          Information we collect when you apply for employment with us or
          provide services to us:
        </b>{' '}
        If you apply for a job with HOVER or provide services to or communicate
        with HOVER as a vendor or an independent contractor, we may collect
        information such as contact information, identifiers such as social
        security number, background information, work availability and
        preferences, employment qualifications and history, and billing and
        payment information. HOVER collects that information to hire or engage
        the best qualified applicants or service providers, to evaluate and
        conduct business with vendors, and to comply with applicable laws.
      </Body>
    </SectionContainer>
    <SectionContainer
      id="privacy-how-we-use"
      heading="How we use your information"
    >
      <Body mb={500}>
        <b>To provide services:</b> We use information we collect to deliver our
        services, including to allow you to log into and access your HOVER
        account, profile, and property information, to process payments, and to
        fulfill our obligations in our{' '}
        <Link href="/terms-of-use">Terms of Use</Link>.
      </Body>
      <Body mb={500}>
        <b>For our business operations:</b>We also use your information to
        ensure our services are working as intended, such as tracking outages or
        troubleshooting issues that you report to us, to make improvements to
        our services, and to review and process employment applications.
      </Body>
      <Body mb={500}>
        <b>To develop new services:</b>We use information we collect in existing
        services to help us develop new ones.
      </Body>
      <Body mb={500}>
        <b>To provide personalized services and marketing:</b>We use the
        information we collect to customize our services for you, including
        providing recommendations and personalized content.
      </Body>
      <Body mb={500}>
        <b>To measure performance:</b>We use data for analytics and measurement
        to understand how our services are used. For example, we analyze data
        about your visits to our sites to do things like optimize product
        design.
      </Body>
      <Body mb={500}>
        <b>To communicate with you:</b>We use information we collect, like your
        email address and your phone number, to interact with you directly. For
        example, we may let you know about upcoming changes or improvements to
        our services. And if you contact HOVER, we’ll keep a record of your
        request in order to help solve any issues you might be facing.
      </Body>
      <Body mb={500}>
        <b>To protect HOVER and our users:</b>We use information to help improve
        the safety and reliability of our services. This includes detecting,
        preventing, and responding to fraud, abuse, security risks, and
        technical issues that could harm HOVER or our users.
      </Body>
      <Body mb={500}>
        We may combine the information we collect among our services and across
        your devices for the purposes described above.
      </Body>
    </SectionContainer>
    <SectionContainer
      id="privacy-when-we-share-and-use"
      heading="When we share your information"
    >
      <Body mb={500}>
        We share your information in the following circumstances:
      </Body>
      <Body mb={500}>
        <b>With your consent:</b> We share your personal information outside of
        HOVER based on your consent or instructions.. For example, if you tell
        us you’d like to be connected to a home-improvement contractor, we may
        share your name, contact information, and information about your home
        with one or more contractors in your area. Or if you choose to use an
        integration between HOVER and another provider, your information will be
        shared with that provider and handled according to its terms of use and
        privacy policy.
      </Body>
      <Body mb={500}>
        <b>With professionals that use HOVER in providing services to you:</b>{' '}
        If a professional, such as a home-improvement contractor or an insurance
        adjuster, uses HOVER to collect information about a home that you own or
        in which you reside (which may include your personal information), that
        information will be accessible to the professional and may also be
        accessible to an organization that employs or contracts with that
        professional (such as a home-improvement company or an insurance carrier
        with which you have a policy).
      </Body>
      <Body mb={500}>
        <b>With account administrators:</b> If you work for an organization that
        uses HOVER services, your account administrator will have access to your
        HOVER account. They may be able to: access and retain information stored
        in your account; view statistics regarding your account; change your
        account password; suspend or terminate your account access; and receive
        your account information in order to satisfy applicable law, regulation,
        legal process, or enforceable governmental request.
      </Body>
      <Body mb={500}>
        <b>With a HOVER partner under an enterprise agreement:</b> If your use
        of HOVER is being sponsored or paid for by a HOVER partner, including if
        you are using a HOVER App sponsored by a partner, HOVER provides
        information about your use of HOVER to that partner. The partner will
        have the ability to access, disclose, restrict, monitor, compile
        information about, and generate reports from your information and your
        general usage of HOVER, and it may use the information for its own
        business and marketing purposes.
      </Body>
      <Body mb={500} isBold>
        If you do not agree to the sharing of your information or do not wish to
        be contacted by the HOVER partner, or its affiliates or service
        providers, do not use HOVER in this manner.
      </Body>
      <Body mb={500}>
        <b>For external processing:</b> We provide personal information to
        service providers to process it for us, based on our instructions and in
        compliance with our Privacy Policy and any other appropriate
        confidentiality and security measures. For example, we use service
        providers to help store our data in data centers, deliver our products
        and services, improve our internal business processes, and offer
        additional support to customers and users.
      </Body>
      <Body mb={500}>
        <b>For legal reasons:</b> We will disclose personal information outside
        of HOVER if we have a good-faith belief that access, use, preservation,
        or disclosure of the information is reasonably necessary to: enforce
        applicable Terms of Use, including investigation of potential
        violations; detect, prevent, or otherwise address fraud, security, or
        technical issues; protect against harm to the rights, property, or
        safety of HOVER, our users, or the public as required or permitted by
        law; and meet any applicable law, regulation, legal process, or
        enforceable governmental request.
      </Body>
      <Body>
        <b>If our business changes:</b> If we are involved in a merger,
        acquisition, or sale of assets, we will continue to take measures to
        protect the confidentiality of personal information and give affected
        users notice before transferring any personal information to a new
        entity.
      </Body>
    </SectionContainer>
    <SectionContainer
      id="privacy-third-party-tools"
      heading="Third Party Tools and Cookies"
    >
      <Body mb={500}>
        <b>Third-Party Tools:</b> We use automated tools and applications to
        operate and evaluate usage of our Service. We use these tools to help us
        improve the Service, performance, and user experiences.
      </Body>
      <Body mb={500}>
        <b>Cookies:</b> Cookies are small text files used to store data for a
        variety of purposes.
      </Body>
      <Body mb={500}>
        The HOVER mobile app and website utilize functional cookies to enable
        the HOVER services and enhance the user experience such as maintaining
        account settings, login and authentication during logged-in periods.
        These cookies do not interact with browsing activity on non-HOVER
        platforms such as third party websites.
      </Body>
      <Body mb={500}>
        The HOVER website utilizes, when enabled, advertising cookies to inform
        targeted advertising initiatives. These cookies further track
        advertising performance to deliver relevant HOVER products and services
        through third party platforms.
      </Body>
      <Body mb={500}>
        To learn more about cookies generally, visit{' '}
        <Link href="https://www.allaboutcookies.org/" fontWeight={400}>
          https://www.allaboutcookies.org/
        </Link>
        . To learn more about the various cookies engaged by the HOVER services,
        and to control your cookie settings visit our Cookie Policy or your
        Cookies Settings.
      </Body>
      <Body>
        You may also opt out of Google Analytics by using the&nbsp;
        <Link href="https://myadcenter.google.com/?sasb=true" fontWeight={400}>
          Google Ad Center page
        </Link>{' '}
        or installing a{' '}
        <Link href="https://tools.google.com/dlpage/gaoptout" fontWeight={400}>
          Google Analytics Opt-out Browser Add-on
        </Link>
        .
      </Body>
      <Body>
        You can also manage your cookies locally by adjusting your browser
        settings, or you can opt-out of targeted advertising through cookies by
        visiting
        <Link
          href="https://www.networkadvertising.org/choices"
          fontWeight={400}
        >
          networkadvertising.org/choices
        </Link>
        &nbsp; or&nbsp;
        <Link href="https://www.aboutads.info/choices" fontWeight={400}>
          aboutads.info/choices
        </Link>
        .
      </Body>
      <Body>
        We do not sell your personal information in exchange for money. Certain
        jurisdictions provide you with a right to opt out of the “sale” or
        sharing of your personal information to third parties in exchange for
        valuable consideration. Our use of analytics or online advertising
        tools, as described above and in our Cookie Policy, may result in the
        disclosure of your information to our third-party partners that are
        subject to this right. You may exercise your right to opt out through
        your Cookie Settings found on&nbsp;
        <Link href="http://www.hover.to" fontWeight={400}>
          www.hover.to
        </Link>
        .
      </Body>
    </SectionContainer>
    <SectionContainer
      id="privacy-third-party-links"
      heading="Third-Party Links"
    >
      <Body>
        Our Service may contain links to third-party websites or social media
        outlets. Any access to and use of such linked websites is not governed
        by this Policy but instead is governed by the privacy policies of those
        third-party websites. We are not responsible for the information
        practices of such third-party websites.
      </Body>
    </SectionContainer>
    <SectionContainer id="privacy-security" heading="Security">
      <Body mb={500}>
        We are committed to protecting your information. We have implemented
        technical and organizational measures intended to achieve an appropriate
        level of security in order to protect against accidental or unlawful
        destruction, loss, alteration, unauthorized disclosure of, or access to
        personal data transmitted, stored, or otherwise processed by HOVER.
        However, no security measures are perfect or impenetrable, and we cannot
        guarantee the security of your information.
      </Body>
      <Body>
        Please take appropriate steps to keep your information secure; use a
        strong password and do not disclose it to others. If you believe there
        has been any unauthorized access to your account, please alert us
        immediately.
      </Body>
    </SectionContainer>
    <SectionContainer
      id="privacy-data-retention"
      heading="Data Retention and Deletion"
    >
      <Body>
        We will delete your personal information upon request. You can make a
        request to delete your personal information using this{' '}
        <Link href="https://preferences.hover.to/privacy" fontWeight={400}>
          Web Form
        </Link>
        . We’ll keep this data in your HOVER Account until you remove it or
        choose to have it removed.
      </Body>
      <Body>
        Subject to privacy laws, we will retain certain information associated
        with your account for analytical purposes and recordkeeping integrity,
        as well as to prevent fraud, collect any fees owed, enforce our Terms of
        Use, take actions we deem necessary to protect the integrity of our
        website or our users, or take other actions otherwise permitted by law.
      </Body>
      <Body>
        In some cases, we may de-identify, aggregate, or otherwise anonymize
        personal information, or retain solely for internal purposes consistent
        with California Privacy Rights Act § 1798.105(c)-(d) and other relevant
        privacy laws or regulations.
      </Body>
    </SectionContainer>
    <SectionContainer id="privacy-children" heading="Children">
      <Body>
        HOVER is targeted to adults, and we do not knowingly collect personal
        information from children. If you believe we have collected personal
        information from a child without parental consent under the permissible
        age of your jurisdiction, please contact us immediately at{' '}
        <Link fontWeight={400} href="mailto:privacy@hover.to">
          privacy@hover.to
        </Link>{' '}
        and we will take reasonable steps to delete it as quickly as possible.
      </Body>
    </SectionContainer>
    <SectionContainer
      id="privacy-your-california"
      heading="Additional California Privacy Notice"
    >
      <Body mb={500}>
        This Privacy Policy and our practices comply with the California Privacy
        Rights Act (CPRA), which requires that we provide California residents
        with notice that you have the right to:
      </Body>
      <UnorderedList mb={500}>
        <ListItem>
          opt-out of the sale or sharing of personal information,
        </ListItem>
        <ListItem>
          know about personal information collected, disclosed or sold,
        </ListItem>
        <ListItem>request deletion of personal information,</ListItem>
        <ListItem>
          correct inaccurate personal information that we maintain about you,
          and
        </ListItem>
        <ListItem>
          be treated without discrimination should you exercise these rights
        </ListItem>
      </UnorderedList>
      <Body mb={500}>
        Do Not Track (“DNT”) is a privacy preference that may be enabled on
        certain web browsers. We are committed to providing you with meaningful
        opt out mechanisms through your Cookie Settings. We do not currently
        recognize or respond to browser-based DNT signals.
      </Body>
      <Body mb={500}>
        California residents have the right under the “Shine the Light” law to
        ask us one time each year if we have shared personal information with
        third parties for their direct marketing purposes. To make such a
        request, please use our Web Form and indicate in your communication that
        you are making a “Shine the Light” inquiry.
      </Body>
    </SectionContainer>
    <SectionContainer
      id="privacy-for-data-subjects"
      heading="For Data Subjects in the European Economic Area (“EEA”) and the United Kingdom (“UK”)"
    >
      <Body>
        The information above describes the types of personal information we
        collect, how we get it, and our legal bases for using it (e.g., your
        consent, our contractual or legal obligations, or our legitimate
        interests). It also describes the circumstances under which we may share
        your personal information.
      </Body>
      <Body mb={500}>
        If you have provided us with your consent to process information, you
        may revoke your consent at any time through our cookie-preferences tool,
        or by submitting a request to delete your personal information.
      </Body>
      <Body>Under data protection law, you have rights including:</Body>
      <UnorderedList ml={0} mb={500}>
        <ListItem>
          <b>Your right of access</b>: You have the right to ask us for copies
          of your personal information.
        </ListItem>
        <ListItem>
          <b>Your right to rectification</b>: You have the right to ask us to
          rectify personal information you think is inaccurate. You also have
          the right to ask us to complete information you think is incomplete.
        </ListItem>
        <ListItem>
          <b>Your right to erasure</b>: You have the right to ask us to erase
          your personal information in certain circumstances.
        </ListItem>
        <ListItem>
          <b>Your right to restriction of processing</b>: You have the right to
          ask us to restrict the processing of your personal information in
          certain circumstances.
        </ListItem>
        <ListItem>
          <b>Your right to object to processing</b>: You have the right to
          object to the processing of your personal information in certain
          circumstances.
        </ListItem>
        <ListItem>
          <b>Your right to data portability</b>: You have the right to ask that
          we transfer the personal information you gave us to another
          organization, or to you, in certain circumstances.
        </ListItem>
      </UnorderedList>
      <Body>
        You are not required to pay any charge for exercising your rights. If
        you make a request, we have one month to respond to you. If you are
        unhappy with how we have used your data, you may complain to your Data
        Protection Authority.
      </Body>
      <Body>
        HOVER is based in the United States (“U.S.”) and therefore, personal
        data of EU data subjects will be transferred to the U.S. HOVER has
        ensured that such transfers and protection of personal data are made in
        accordance with European Union and other local data protection laws and
        regulations. Standard contractual clauses, if relied upon, are concluded
        with all relevant parties. You can request a copy of the standard
        contractual clauses at any time by contacting us. For any further
        questions on international data transfers, you can contact us; our
        contact details can be found below.
      </Body>
    </SectionContainer>

    <SectionContainer
      id="privacy-rep-for-eu-uk"
      heading="Representative for data subjects in the EU and UK"
    >
      <Body mb={500}>
        We value your privacy and your rights as a data subject and have
        therefore appointed Prighter Group with its local partners as our
        privacy representative and your point of contact. Prighter gives EU and
        UK data subjects an easy way to exercise privacy-related rights (e.g.
        requests to access or erase personal data). To contact us through or
        make use of data subject rights under the EU or UK jurisdictions, please
        visit the following{' '}
        <Link href="https://prighter.com/q/11180699225" fontWeight={400}>
          website.
        </Link>
      </Body>
    </SectionContainer>

    <SectionContainer
      id="privacy-privacy-request"
      heading="How To Make A Privacy Request"
    >
      <Body mb={500}>
        If you wish to exercise your rights, please use this{' '}
        <Link href="https://preferences.hover.to/privacy" fontWeight={400}>
          Web Form.
        </Link>
      </Body>
      <Body>
        To exercise your California right-to-know as described above, you may
        also call us at our toll-free number (866-665-0340).
      </Body>
    </SectionContainer>
    <SectionContainer id="privacy-contact-us" heading="Contact us">
      <Body mb={500}>
        You may contact us with any questions relating to this Privacy Policy by
        contacting us at{' '}
        <Link fontWeight={400} href="mailto:privacy@hover.to">
          privacy@hover.to
        </Link>
      </Body>
      <Body>HOVER Inc.</Body>
      <Body>255 Bush St, 7th Floor</Body>
      <Body>San Francisco, CA 94104</Body>
    </SectionContainer>

    <Box display="flex" mb={800} gap={16}>
      <a href="https://prighter.com/q/11180699225">
        <img
          width={150}
          src="https://prighter.com/certificateofrepresentation/11180699225/getwebcertificate.png"
          alt="Prighter certificate of Art 27 representation"
        />
      </a>
      <a href="https://prighter.com/q/11180699225">
        <img
          width={150}
          src="https://prighter.com/ukrep/11180699225/getwebcertificate.png"
          alt="PrighterUKRep certificate of Art 27 representation"
        />
      </a>
    </Box>
  </>
);
